import { red } from '@mui/material/colors';
import { createTheme, Theme } from '@mui/material/styles';

type PaletteMode = 'light' | 'dark';

// Shared, mode-independent typography.
const typography = {
  fontFamily: '"Inter", "Helvetica Neue", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 300,
  fontWeightMedium: 400,
  fontWeightBold: 600,
  fontSize: 16,
  h1: { fontSize: '2.5rem', fontWeight: 300 },
  h2: { fontSize: '2rem', fontWeight: 300 },
  h3: { fontSize: '1.5rem', fontWeight: 300 },
  h4: { fontSize: '1.25rem', fontWeight: 300 },
  h5: { fontSize: '1.125rem', fontWeight: 300 },
  h6: { fontSize: '1rem', fontWeight: 300 },
  body1: { fontSize: '1rem', fontWeight: 300 },
  body2: { fontSize: '0.875rem', fontWeight: 300 },
} as const;

/**
 * Builds the MUI theme for a given color-scheme mode.
 *
 * Surface colors are bridged to the app's Tailwind CSS variables (defined in
 * `index.css` for `:root` and `.dark`) so MUI surfaces match the rest of the UI.
 * `palette.mode` drives MUI's derived behaviors (secondary text, dividers,
 * inputs, hover/disabled overlays) so components render correctly in dark mode.
 *
 * Text and brand colors stay concrete because MUI runs them through color
 * manipulation (`alpha`, contrast-text) — e.g. Button computes
 * `alpha(palette.text.primary, …)` — which cannot parse `var(...)` values.
 * The concrete text colors are chosen to match the `--foreground` token.
 */
export function createAppTheme(mode: PaletteMode = 'light'): Theme {
  const isDark = mode === 'dark';
  return createTheme({
    spacing: 8,
    palette: {
      mode,
      primary: {
        main: '#3995f6',
      },
      secondary: {
        main: '#333333',
      },
      error: {
        main: red.A400,
      },
      background: {
        default: 'var(--background)',
        paper: 'var(--card)',
      },
      text: {
        // Matches --foreground: light oklch(0.20 0.01 250) / dark oklch(0.95 0.02 250).
        primary: isDark ? '#eceef3' : '#2b2d33',
      },
    },
    typography,
  });
}

// Default export remains the light theme for backward compatibility.
const theme: Theme = createAppTheme('light');

export default theme;
